import React, { createContext, useContext, useReducer, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useLocation } from "react-router-dom";

export type Tier = "starter" | "core" | "premium" | "measure" | "enable";

export interface TierFeatures {
	maxFacilities: number;
	maxUsers: number;
	canGenerateReports: boolean;
	canRunSimulations: boolean;
	hasAdvancedReporting: boolean;
	hasComplianceReporting: boolean;
	hasBenchmarking: boolean;
	hasGoalTracking: boolean;
	hasApiAccess: boolean;
	hasPrioritySupport: boolean;
	hasDedicatedAM: boolean;
	hasWhiteLabel: boolean;
	hasCustomIntegrations: boolean;
	hasSLA: boolean;
	dashboardCharts: "top-only" | "all";
	// Segmentation fence flags (mirror backend config/tierFeatures.ts)
	scope3ActivityModules: boolean;
	spendBasedScope3: boolean;
	dataPackExport: boolean;
	marketBasedScope2: boolean;
	customEmissionFactors: boolean;
	assuranceRegisters: boolean;
	baselining: boolean;
	scope3Categories: boolean;
	s2Report: boolean;
	supplierRequests: boolean;
	multiEntityConsolidation: boolean;
	dataQualityScore: boolean;
}

interface SubscriptionState {
	currentPlan: Tier | null;
	tierFeatures: TierFeatures | null;
	status: "active" | "past_due" | "canceled" | "loading" | null;
	isLoading: boolean;
	error: string | null;
	hasActiveSubscription: boolean;
	stripeCustomerId: string | null;
}

interface SubscriptionContextType {
	state: SubscriptionState;
	refreshSubscription: () => Promise<SubscriptionState | null>;
	hasFeature: (feature: keyof TierFeatures) => boolean;
	isPremium: () => boolean;
	isCore: () => boolean;
	isStarter: () => boolean;
}

type SubscriptionAction =
	| { type: "LOADING" }
	| { type: "SUCCESS"; payload: any }
	| { type: "ERROR"; payload: string }
	| { type: "RESET" };

const initialState: SubscriptionState = {
	currentPlan: null,
	tierFeatures: null,
	status: "loading",
	isLoading: true,
	error: null,
	hasActiveSubscription: false,
	stripeCustomerId: null,
};

const normalizeTier = (value: any): Tier => {
	if (
		value === "core" ||
		value === "premium" ||
		value === "measure" ||
		value === "enable"
	)
		return value;
	return "starter";
};

const subscriptionReducer = (
	state: SubscriptionState,
	action: SubscriptionAction,
): SubscriptionState => {
	switch (action.type) {
		case "LOADING":
			return { ...state, isLoading: true, error: null };
		case "SUCCESS": {
			const tier = normalizeTier(
				action.payload.tier ?? action.payload.plan,
			);
			return {
				...state,
				isLoading: false,
				error: null,
				currentPlan: tier,
				tierFeatures: action.payload.tierFeatures ?? null,
				status: action.payload.status,
				hasActiveSubscription: action.payload.hasActiveSubscription,
				stripeCustomerId: action.payload.stripeCustomerId,
			};
		}
		case "ERROR":
			return { ...state, isLoading: false, error: action.payload };
		case "RESET":
			return initialState;
		default:
			return state;
	}
};

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(
	undefined,
);

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({
	children,
}) => {
	const [state, dispatch] = useReducer(subscriptionReducer, initialState);
	const location = useLocation();
	const {
		getAccessTokenSilently,
		isAuthenticated,
		isLoading: authLoading,
		user,
	} = useAuth0();

	const refreshSubscription = async (): Promise<SubscriptionState | null> => {
		if (!isAuthenticated || authLoading) {
			return null;
		}

		const userMetadata = user?.["https://arteh.com.au/user_metadata"];
		const appMetadata = user?.["https://arteh.com.au/app_metadata"];

		const hasCompany =
			userMetadata?.activeCompanyId ||
			(appMetadata?.companies && appMetadata.companies.length > 0);

		if (!hasCompany) {
			dispatch({ type: "RESET" });
			return null;
		}

		dispatch({ type: "LOADING" });

		try {
			let token;
			try {
				token = await getAccessTokenSilently();
			} catch (tokenError: any) {
				dispatch({ type: "RESET" });
				return null;
			}

			const response = await fetch(
				`${process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN}/api/subscription/status`,
				{
					headers: {
						Authorization: `Bearer ${token}`,
						"Content-Type": "application/json",
					},
				},
			);

			if (!response.ok) {
				if (response.status === 401 || response.status === 403) {
					dispatch({ type: "RESET" });
					return null;
				}
				throw new Error(`HTTP error! status: ${response.status}`);
			}

			const data = await response.json();
			const subscriptionData = data.Data || data;

			dispatch({ type: "SUCCESS", payload: subscriptionData });

			return {
				currentPlan: normalizeTier(
					subscriptionData.tier ?? subscriptionData.plan,
				),
				tierFeatures: subscriptionData.tierFeatures ?? null,
				status: subscriptionData.status,
				isLoading: false,
				error: null,
				hasActiveSubscription: subscriptionData.hasActiveSubscription,
				stripeCustomerId: subscriptionData.stripeCustomerId,
			};
		} catch (error: any) {
			if (
				!error.message?.includes("401") &&
				!error.message?.includes("403")
			) {
				console.error("Failed to refresh subscription:", error);
			}
			dispatch({
				type: "ERROR",
				payload: error.message || "Failed to load subscription status",
			});
			return null;
		}
	};

	useEffect(() => {
		let mounted = true;
		if (isAuthenticated && !authLoading) {
			refreshSubscription().catch((error) => {
				if (mounted && !error.message?.includes("403")) {
					console.error("Subscription refresh failed:", error);
				}
			});
		} else if (!isAuthenticated) {
			dispatch({ type: "RESET" });
		}
		return () => {
			mounted = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isAuthenticated, authLoading]);

	useEffect(() => {
		if (
			location.pathname === "/dashboard" &&
			isAuthenticated &&
			!authLoading
		) {
			refreshSubscription().catch((error) => {
				if (!error.message?.includes("403")) {
					console.error(
						"Dashboard subscription refresh failed:",
						error,
					);
				}
			});
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [location.pathname, isAuthenticated, authLoading]);

	const hasFeature = (feature: keyof TierFeatures): boolean => {
		const f = state.tierFeatures;
		if (!f) return false;
		const value = f[feature];
		if (typeof value === "boolean") return value;
		if (typeof value === "number") return value === -1 || value > 0;
		return false;
	};

	const isPremium = (): boolean =>
		state.currentPlan === "premium" && state.status === "active";

	const isCore = (): boolean =>
		state.currentPlan === "core" && state.status === "active";

	const isStarter = (): boolean =>
		state.currentPlan === "starter" || state.currentPlan === null;

	const contextValue: SubscriptionContextType = {
		state,
		refreshSubscription,
		hasFeature,
		isPremium,
		isCore,
		isStarter,
	};

	return (
		<SubscriptionContext.Provider value={contextValue}>
			{children}
		</SubscriptionContext.Provider>
	);
};

export const useSubscription = (): SubscriptionContextType => {
	const context = useContext(SubscriptionContext);
	if (!context) {
		throw new Error(
			"useSubscription must be used within SubscriptionProvider",
		);
	}
	return context;
};

export const withFeatureGate = <P extends object>(
	WrappedComponent: React.ComponentType<P>,
	requiredFeature: keyof TierFeatures,
	fallbackComponent?: React.ComponentType,
) => {
	return (props: P) => {
		const { hasFeature } = useSubscription();

		if (hasFeature(requiredFeature)) {
			return <WrappedComponent {...props} />;
		}

		if (fallbackComponent) {
			const FallbackComponent = fallbackComponent;
			return <FallbackComponent />;
		}

		return null;
	};
};

export const PremiumFeature: React.FC<{
	children: React.ReactNode;
	fallback?: React.ReactNode;
}> = ({ children, fallback = null }) => {
	const { isPremium } = useSubscription();
	return isPremium() ? <>{children}</> : <>{fallback}</>;
};
